import { Building2, Send } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import {
  apartmentTypeOptions, currencyOptions, formatApartmentType,
  type ApartmentType, type CurrencyCode, type ListingType,
} from "@/lib/listings";
import { toast } from "@/hooks/use-toast";
import { apartmentRequestSchema, normalizeWhatsapp } from "@/lib/validation";
import { normalizeDigits } from "@/lib/digits";
import { AreaPicker, resolveArea } from "@/components/AreaPicker";
import { OTHER_AREA_VALUE } from "@/data/istanbulAreas";
import { PhoneInput } from "@/components/PhoneInput";
import { SEO } from "@/lib/seo";

const RequestApartment = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    whatsapp_number: "",
    email: "",
    listing_type: "rent" as ListingType,
    apartment_type: "2+1" as ApartmentType,
    apartment_type_note: "",
    area_value: "",
    area_other: "",
    budget_min: "",
    budget_max: "",
    currency: "USD" as CurrencyCode,
    notes: "",
  });

  const update = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((s) => ({ ...s, [key]: value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const area = resolveArea(form.area_value, form.area_other);
    const parsed = apartmentRequestSchema().safeParse({
      full_name: form.full_name,
      whatsapp_number: form.whatsapp_number,
      email: form.email,
      apartment_type: form.apartment_type,
      apartment_type_note: form.apartment_type_note,
      currency: form.currency,
      area,
      budget_min: form.budget_min,
      budget_max: form.budget_max,
      notes: form.notes,
    });
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      toast({ title: first.message, variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("apartment_requests").insert({
      full_name: form.full_name.trim(),
      whatsapp_number: normalizeWhatsapp(form.whatsapp_number),
      email: form.email.trim() || null,
      listing_type: form.listing_type,
      apartment_type: form.apartment_type,
      apartment_type_note: form.apartment_type === "other" ? form.apartment_type_note.trim() : null,
      area,
      budget_min: form.budget_min ? Number(normalizeDigits(form.budget_min)) : null,
      budget_max: form.budget_max ? Number(normalizeDigits(form.budget_max)) : null,
      currency: form.currency,
      notes: form.notes.trim() || null,
    });
    setSubmitting(false);
    if (error) {
      // Log technical details for admins, but never show raw DB errors to users.
      void supabase.rpc("log_client_error", {
        _message: `apartment_requests insert failed: ${error.message}`,
        _context: { code: error.code, hint: error.hint, page: "/request" },
        _kind: "error_log",
      });
      toast({
        title: t("request.errorTitle"),
        description: t("request.errorDesc"),
        variant: "destructive",
      });
      return;
    }
    toast({ title: t("request.successTitle"), description: t("request.successDesc") });
    setTimeout(() => navigate("/"), 2500);
  };


  return (
    <section className="min-h-screen bg-background px-4 py-12 sm:px-6 lg:px-8">
      <SEO title={t("seo.request.title")} description={t("seo.request.description")} path="/request" />
      <div className="mx-auto max-w-2xl">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-card/60 px-4 py-2 text-sm font-semibold text-primary">
          <Building2 className="h-4 w-4" /> {t("request.badge")}
        </div>
        <h1 className="text-3xl font-black sm:text-4xl">{t("request.title")}</h1>
        <p className="mt-2 text-muted-foreground">{t("request.subtitle")}</p>

        <form onSubmit={submit} className="mt-8 space-y-4 rounded-2xl border border-border bg-card p-6 shadow-soft">
          <Field label={t("request.fullName")}>
            <input value={form.full_name} onChange={(e) => update("full_name", e.target.value)} required className={inputCls} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("request.whatsapp")}>
              <PhoneInput value={form.whatsapp_number} onChange={(v) => update("whatsapp_number", v)} required />
            </Field>
            <Field label={t("request.emailOptional")}>
              <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} />
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("request.listingType")}>
              <div className="flex gap-2">
                {(["rent", "sale"] as ListingType[]).map((v) => (
                  <button type="button" key={v} onClick={() => update("listing_type", v)}
                    className={`flex-1 rounded-md border px-3 py-2 text-sm font-bold ${form.listing_type === v ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background"}`}>
                    {t(v === "sale" ? "request.type.buy" : "request.type.rent")}
                  </button>
                ))}
              </div>
            </Field>
            <Field label={t("request.apartmentType")}>
              <select value={form.apartment_type} onChange={(e) => update("apartment_type", e.target.value as ApartmentType)} className={inputCls}>
                {apartmentTypeOptions.map((o) => <option key={o} value={o}>{formatApartmentType(o)}</option>)}
              </select>
            </Field>
          </div>

          {form.apartment_type === "other" && (
            <Field label={t("field.apartmentType.specifyOther")}>
              <input value={form.apartment_type_note} onChange={(e) => update("apartment_type_note", e.target.value)} required className={inputCls} />
            </Field>
          )}

          <Field label={t("request.preferredArea")}>
            <AreaPicker
              value={form.area_value}
              otherText={form.area_other}
              onChange={(v, other) => setForm((s) => ({ ...s, area_value: v, area_other: other }))}
              required={form.area_value === OTHER_AREA_VALUE}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label={t("request.budget.minOptional")}>
              <input inputMode="numeric" value={form.budget_min} onChange={(e) => update("budget_min", e.target.value)} className={inputCls} />
            </Field>
            <Field label={t("request.budget.maxOptional")}>
              <input inputMode="numeric" value={form.budget_max} onChange={(e) => update("budget_max", e.target.value)} className={inputCls} />
            </Field>
            <Field label={t("request.currency")}>
              <select value={form.currency} onChange={(e) => update("currency", e.target.value as CurrencyCode)} className={inputCls}>
                {currencyOptions.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <p className="rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-muted-foreground">{t("request.budget.helperNote")}</p>

          <Field label={t("request.notes")}>
            <textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={4} className={inputCls} />
          </Field>

          <button disabled={submitting} className="flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 font-black text-primary-foreground shadow-premium disabled:opacity-60">
            <Send className="h-4 w-4" /> {submitting ? t("request.sending") : t("request.submit")}
          </button>
        </form>
      </div>
    </section>
  );
};

const inputCls = "w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none";

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-muted-foreground">{label}</span>
    {children}
  </label>
);

export default RequestApartment;
